import React from 'react';
import { Content, List, ListComponent, ListItem, OrderType } from '@patternfly/react-core';
import { CopyableCodeBlock } from './CopyableCodeBlock';

interface DeployInstructionsRhelProps {
  envFileContent: string;
  hasCredentials: boolean;
}

/**
 * The RHEL-based (DNF) deployment instructions for the Linux PHP agent — the original content of
 * the "Deploy the agent" card, unchanged, now living in the "RHEL based" platform tab.
 */
export const DeployInstructionsRhel: React.FC<DeployInstructionsRhelProps> = ({ envFileContent, hasCredentials }) => (
  <>
    <Content component="p">
      Run these steps as root on each server that should report to Ancalagon Manager. The agent is read-only: it only
      collects and transmits system information, and never modifies the host.
    </Content>
    <List component={ListComponent.ol} type={OrderType.number}>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Install the agent.</strong> Set up the Ancalagon DNF repository, then install the package (requires
          PHP 8.5+ with the <code>curl</code>, <code>posix</code> and <code>libvirt</code> extensions):
        </Content>
        <CopyableCodeBlock id="agent-install-copy" code="dnf install manager-agent" copyLabel="Copy install command" />
      </ListItem>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Configure the agent.</strong> Write this configuration — already filled in for your organization —
          to <code>/etc/ancalagon/manager-agent.env</code>:
        </Content>
        <CopyableCodeBlock id="agent-env-copy" code={envFileContent} copyLabel="Copy configuration" />
        {!hasCredentials && (
          <Content component="small">
            Replace <code>&lt;your-client-secret&gt;</code> with the client secret you saved when the agent was
            enabled.
          </Content>
        )}
        <Content component="small">
          Prefer a guided setup? Run <code>php /opt/ancalagon/manager-agent/bin/agent.php --setup</code> and enter the
          values above when prompted.
        </Content>
      </ListItem>
      <ListItem className="pf-v6-u-mb-md">
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Test the agent.</strong> Do a dry run first (collects and prints the report without sending
          anything), then send a first real report:
        </Content>
        <CopyableCodeBlock
          id="agent-test-copy"
          code={
            'php /opt/ancalagon/manager-agent/bin/agent.php --dry-run\nphp /opt/ancalagon/manager-agent/bin/agent.php -v'
          }
          copyLabel="Copy test commands"
        />
      </ListItem>
      <ListItem>
        <Content component="p" className="pf-v6-u-mb-sm">
          <strong>Enable hourly reporting.</strong> Turn on the systemd timer so the agent reports every hour, and
          check its status:
        </Content>
        <CopyableCodeBlock
          id="agent-timer-copy"
          code={'manager-agent --set-auto\nsystemctl status manager-agent.timer'}
          copyLabel="Copy timer commands"
        />
      </ListItem>
    </List>
  </>
);
